import { asyncWithLDProvider, LDProvider as LDProviderComponent } from 'launchdarkly-react-client-sdk';
import { type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import './riveRuntime';
import './i18n';

import './theme/app.css'; // must be imported before App!

import { ConfigProvider } from '@helios/frontend-core';
import App from './components/_app/App/App';
import { bootIntercom } from './components/IntercomBubble/IntercomBubble';
import { coreLibConfig, LDFlagKeys } from './config';
import { defaultLDFlagValues } from './config/ldFlagDefaults';
import { ChatProvider } from './contexts/ChatContext';
import * as serviceWorker from './serviceWorker';
import { startInitialLiveUpdateSync } from './utils/initialLiveUpdateSync';

const isLDEnabled = process.env.VITE_ENABLE_LAUNCH_DARKLY === 'true';

startInitialLiveUpdateSync();

(async () => {
  if (process.env.VITE_USE_MOCKS === 'true') {
    const { startDevMockWorker } = await import('./mocks/devServer');
    await startDevMockWorker();
  }

  const allFlagsEnabled = Object.values(LDFlagKeys).reduce<Record<string, boolean>>(
    (flags, key) => ({ ...flags, [key]: defaultLDFlagValues[key] ?? true }),
    {},
  );

  const LDProvider = isLDEnabled
    ? await asyncWithLDProvider({
        clientSideID: String(process.env.VITE_LAUNCH_DARKLY_CLIENT_ID),
        options: {
          bootstrap: 'localStorage',
        },
        user: {
          anonymous: true,
          key: 'unknown-user-key',
        },
      })
    : ({ children }: { children: ReactNode }) => (
        <LDProviderComponent clientSideID="" deferInitialization options={{ bootstrap: allFlagsEnabled }}>
          {children}
        </LDProviderComponent>
      );
  bootIntercom();

  const container = document.getElementById('root');
  if (!container) throw new Error('Failed to find the root element');
  const root = createRoot(container);
  root.render(
    <ConfigProvider config={coreLibConfig}>
      <LDProvider>
        <ChatProvider>
          <App />
        </ChatProvider>
      </LDProvider>
    </ConfigProvider>,
  );
})();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
